import { useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import HelpDrawer from "../common/HelpDrawer";
import HelpGuideFooterActions from "../common/help/HelpGuideFooterActions";
import InfoTooltip from "../common/InfoTooltip";
import RentalsShell, {
  rentalsHeroSecondaryActionClass,
} from "../../pages/manager/rentals/RentalsShell";
import {
  ADVANCED_RECORDS_HELP_SECTIONS,
  getAdvancedRecordTooltip,
} from "./advancedRecordsHelpContent";
import {
  getWorkspaceDisplayName,
  normalizeWorkspace,
  titleCaseLabel,
} from "../../utils/workspacePresentation";
import {
  getMarketplaceOperatorWorkspaceAgreementsPath,
  getMarketplaceOperatorWorkspaceCompliancePath,
  getMarketplaceOperatorWorkspaceDetailPath,
  getMarketplaceOperatorWorkspaceOwnershipPath,
  getMarketplaceOperatorWorkspaceSupportPath,
  getMarketplaceOperatorWorkspaceWarningsPath,
  getMarketplaceOperatorWorkspacesPath,
} from "../../utils/marketplaceOperatorRoutes";

function buildTabs(workspaceId) {
  return [
    {
      to: getMarketplaceOperatorWorkspaceDetailPath(workspaceId),
      label: "Listing",
    },
    {
      to: getMarketplaceOperatorWorkspaceCompliancePath(workspaceId),
      label: "Setup Checks",
    },
    {
      to: getMarketplaceOperatorWorkspaceOwnershipPath(workspaceId),
      label: "Client & Payment Control",
    },
    {
      to: getMarketplaceOperatorWorkspaceAgreementsPath(workspaceId),
      label: "Agreement Setup",
    },
    {
      to: getMarketplaceOperatorWorkspaceWarningsPath(workspaceId),
      label: "Setup History",
    },
    {
      to: getMarketplaceOperatorWorkspaceSupportPath(workspaceId),
      label: "Support & Follow-up",
    },
  ];
}

export default function WorkspaceComplianceFrame({
  workspace,
  workspaceId,
  title,
  description,
  children,
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpButtonRef = useRef(null);
  const tabs = buildTabs(workspaceId);
  const normalizedWorkspace = normalizeWorkspace(workspace);
  const workspaceName =
    workspace && workspaceId ? getWorkspaceDisplayName(workspace) : `Workspace #${workspaceId}`;
  const workspaceType = titleCaseLabel(normalizedWorkspace.workspace_type, "Not set");
  const listingState = titleCaseLabel(
    normalizedWorkspace.marketplace_status || normalizedWorkspace.visibility,
    "Not set"
  );

  return (
    <RentalsShell
      eyebrow="MyBoothShare Rental Setup"
      title={title}
      description={description}
      actions={
        <>
          <button
            ref={helpButtonRef}
            type="button"
            onClick={() => setHelpOpen(true)}
            className={rentalsHeroSecondaryActionClass}
          >
            Help
          </button>
          <Link
            to={getMarketplaceOperatorWorkspacesPath()}
            className={rentalsHeroSecondaryActionClass}
          >
            Back to Inventory
          </Link>
          <Link
            to={getMarketplaceOperatorWorkspaceDetailPath(workspaceId)}
            className={rentalsHeroSecondaryActionClass}
          >
            Back to Setup
          </Link>
        </>
      }
    >
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Advanced Setup and Records Help Guide"
        intro="Use this guide when you need help deciding which advanced workspace page to open or how to read the fields on these pages."
        sections={ADVANCED_RECORDS_HELP_SECTIONS}
        footer={<HelpGuideFooterActions onClose={() => setHelpOpen(false)} />}
        returnFocusRef={helpButtonRef}
        usePortal
      />
      <section className="scroll-mt-28 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-5 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                  Rental workspace setup
                </p>
                <InfoTooltip
                  text="This area contains deeper records for setup checks, agreements, activity, and support. Use the main setup page for normal listing edits."
                  label="Rental workspace setup"
                  ariaLabel="More information"
                  placement="bottom"
                  maxWidth={320}
                />
              </div>
              <h2 className="mt-2 break-words text-2xl font-semibold text-slate-900">{workspaceName}</h2>
              <p className="mt-2 max-w-3xl break-words text-sm leading-6 text-slate-600">
                These advanced pages support the guided rental listing setup with deeper records,
                agreement controls, and setup history.
              </p>
            </div>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="min-w-0 break-words rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Workspace <span className="font-semibold text-slate-900">#{workspaceId}</span>
              </div>
              <div className="min-w-0 break-words rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Type <span className="font-semibold text-slate-900">{workspaceType}</span>
              </div>
              <div className="min-w-0 break-words rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Listing status <span className="font-semibold text-slate-900">{listingState}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap gap-2 px-4 py-4 sm:px-6">
          {tabs.map((tab) => (
            <div key={tab.to} className="inline-flex items-center gap-1">
              <NavLink
                to={tab.to}
                title={getAdvancedRecordTooltip(tab.label)}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-center text-sm font-semibold transition ${
                    isActive
                      ? "bg-rose-500 text-white"
                      : "border border-slate-300 text-slate-700 hover:border-slate-400"
                  }`
                }
              >
                {tab.label}
              </NavLink>
              <InfoTooltip
                text={getAdvancedRecordTooltip(tab.label)}
                label={tab.label}
                ariaLabel="More information"
                placement="bottom"
                maxWidth={300}
              />
            </div>
          ))}
        </div>
      </section>
      {children}
    </RentalsShell>
  );
}
